import { useCallback, useEffect, useMemo, useState } from "react";
import { NOTIFICATION_STATUSES, NOTIFICATION_TABS } from "../../constants/notification-statuses";
import { USER_ROLES } from "../../constants/user-roles";
import {
  getCurrentUserFromLocalStorage,
  getI18nLanguageFromLocalStorage
} from "../../helpers/localStorageHelper";
import { translate } from "../../localization/translateService";
import {
  getAllNotifications,
  getHubConnection,
  initializeConnection,
  onNotificationsUpdated,
  onListNotificationsUpdated,
  updateNotification
} from "../../services/signalRService";
import logo from "../../stories/assets/logo-keduall.png";

import { Divider } from "primereact/divider";
import { Paginator } from "primereact/paginator";
import { paginatorTemplate } from "../../helpers/paginatorTemplate";
import NoData from "../nodata/NoData";
import { convertUTCDateTimeInMessage } from "../../helpers/convertUTCDateTimeInMessage";
import KeduLoading from "../loading/KeduLoading";
import { TabPanel, TabView } from "primereact/tabview";
import { formatDateV2 } from "../../helpers/dateTimeHelper";
import { DATETIME_FORMATS } from "../../constants/datetime-formats";

export default function NotificationList() {
  const [notifications, setNotifications] = useState({
    userNotifications: [],
    systemNotifications: [],
  });
  const params = new URLSearchParams(window.location.search);
  const defaultTab = params.get("tab") || NOTIFICATION_TABS.USER;
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lang, setLanguage] = useState("");
  const [currentData, setCurrentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [translations, setTranslations] = useState({
    notificationNumber: "",
    notification: "",
    date: "",
    noData: "",
    minuteShortLabel: "",
    hourShortLabel: "",
    dayShortLabel: "",
    ago: "",
    user: "",
    system: "",
  });
  const onPageChange = (event) => {
    setFirst(event.first);
  };

  const tabs = [
    { label: translations.user, value: NOTIFICATION_TABS.USER },
    { label: translations.system, value: NOTIFICATION_TABS.SYSTEM }
  ];

  
  const handleSelect = (v) =>  {
    setSelectedTab(v);
    setFirst(0);
  }

  useEffect(() => {
    const sub = onListNotificationsUpdated.subscribe(batch => {
      if (!batch) return;
      const sortedUser = (batch.userNotifications || []).sort(
        (a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
      );

      const sortedSystem = (batch.systemNotifications || []).sort(
        (a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
      );
      setNotifications({
        userNotifications: sortedUser,
        systemNotifications: sortedSystem,
      });
    });

    (async () => {
      setIsLoading(true);
      try {
        const user = getCurrentUserFromLocalStorage();
        if (!user) {
          return;
        }
        if (!getHubConnection()) {
          await initializeConnection(process.env.API_ENDPOINT);
        }
        await getAllNotifications(user.id, getI18nLanguageFromLocalStorage().toLowerCase(), true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    const language = getI18nLanguageFromLocalStorage();
    setLanguage(language);

    //Language setup init
    const loadTranslations = async () => {
      const notificationNumber = await translate(
        "NOTIFICATIONS.FIELD_LABELS.NOTIFICATION_NO", true
      );
      const notification = await translate(
        "NOTIFICATIONS.FIELD_LABELS.NOTIFICATION", true
      );
      const date = await translate("NOTIFICATIONS.FIELD_LABELS.DATE", true);
      const noData = await translate("MESSAGE.NO_DATA", true);
      const ago = await translate("NOTIFICATIONS.MESSAGE.AGO", true);
      const minuteShortLabel = await translate(
        "TIME_AGO.UNITS.MINUTE_SHORT", true
      );
      const hourShortLabel = await translate(
        "TIME_AGO.UNITS.HOUR_SHORT", true
      );
      const dayShortLabel = await translate(
        "TIME_AGO.UNITS.DAY_SHORT", true
      );
      const user = await translate("NOTIFICATIONS.TABS.USER", true);
      const system = await translate("NOTIFICATIONS.TABS.SYSTEM", true);
      setTranslations({
        notificationNumber,
        notification,
        date,
        noData,
        minuteShortLabel,
        hourShortLabel,
        dayShortLabel,
        ago,
        user,
        system
      });
    };

    loadTranslations();

  }, []);

  useEffect(() => {
    const source =
      selectedTab === NOTIFICATION_TABS.USER
        ? notifications.userNotifications
        : notifications.systemNotifications;
    setTotalRecords(source.length);
    setCurrentData(
      source.slice(first, first + rows)
    );
  }, [notifications, first, rows, selectedTab]);


  const calculateTimeLeft = (createdTime) => {
    // Get the current time in UTC
    const now = new Date().toISOString();
    // Parse the createdTime as UTC
    const createDate = new Date(createdTime + "Z"); // Adding 'Z' to ensure it is treated as UTC
    // Time difference in milliseconds
    const diffInMillis = new Date(now).getTime() - createDate.getTime();

    // Calculate difference in minutes, hours, and days
    const diffInMinutes = Math.floor(diffInMillis / (1000 * 60));
    const diffInHours = Math.floor(diffInMillis / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMillis / (1000 * 60 * 60 * 24));

    // Show time left based on the difference
    if (diffInMinutes < 60) {
      return `${diffInMinutes}${translations.minuteShortLabel}`; // Less than an hour
    } else if (diffInHours < 24) {
      return `${diffInHours}${translations.hourShortLabel}`; // Less than a day
    } else {
      return `${diffInDays}${translations.dayShortLabel}`; // More than a day
    }
  };

  const stripHtmlTags = (input) => {
    if (!input) return "";

    const processedInput = convertUTCDateTimeInMessage(input);

    const doc = new DOMParser().parseFromString(processedInput, "text/html");

    // Return only the text content (strips all HTML elements)
    return doc.body.textContent || "";
  };

  const handleNavigate = async (data) => {
    setIsLoading(true);
    const updatedNotification = {
      id: data.id,
      status: NOTIFICATION_STATUSES.READ, // Mark as READ
    };
    await updateNotification(updatedNotification);
    setNotifications((prev) => ({
      userNotifications: prev.userNotifications.map((n) =>
        n.id === data.id ? { ...n, status: NOTIFICATION_STATUSES.READ } : n
      ),
      systemNotifications: prev.systemNotifications.map((n) =>
        n.id === data.id ? { ...n, status: NOTIFICATION_STATUSES.READ } : n
      ),
    }));
    if (data.pathUrl && data.pathUrl.trim() !== "") {
      if (data.receiver === USER_ROLES.ADMIN) {
        window.location.href = `${process.env.ADMIN_PORTAL_ENDPOINT}${data.pathUrl}`;
      } else if (data.receiver === USER_ROLES.TRAINER) {
        window.location.href = `${process.env.TRAINER_PORTAL_ENDPOINT}${data.pathUrl}`;
      } else if (data.receiver === USER_ROLES.STUDENT) {
        window.location.href = `${process.env.STUDENT_PORTAL_ENDPOINT}${data.pathUrl}`;
      }
    } else {
      window.location.href = `/${lang}/notifications/${data.id}`;
    }
  };

  const notificationUnreadLength = (data) => {
    return data.filter((item) => item.status === NOTIFICATION_STATUSES.UNREAD).length;
  };

  const getUnreadCounts = useCallback((notifications) => {
    const userUnread = notificationUnreadLength(notifications.userNotifications);
    const systemUnread = notificationUnreadLength(notifications.systemNotifications);

    return {
      userUnread,
      systemUnread,
      totalUnread: userUnread + systemUnread,
    };
  }, []);

  const { userUnread, systemUnread } = useMemo(
      () => getUnreadCounts(notifications),
      [notifications, getUnreadCounts]
    );
  
    const unreadMap = {
      [NOTIFICATION_TABS.USER]: userUnread,
      [NOTIFICATION_TABS.SYSTEM]: systemUnread,
    };

  return (
    <div className="flex flex-column align-content-center justify-content-center main-content-wrapper notification-container">
      <div className="page-title-container">
        <h1 className="keduall-typography heading-h1-semibold-600 text-primary-txt-color mb-075 animate__animated animate__fadeInDown">
          {translations.notification}
        </h1>
      </div>
      <div className="keduall-tab-panel">
        <TabView
          activeIndex={tabs.findIndex((t) => t.value === selectedTab)}
          onTabChange={(e) => handleSelect(tabs[e.index].value)}
        >
          {tabs.map((tab) => (
            <TabPanel
              key={tab.value}
              header={`${tab.label} (${unreadMap[tab.value]})`}
            >
              <ul className="notification-list">
                {isLoading ? (
                  <div className="notification-list-loader">
                    <KeduLoading />
                  </div>
                ) : currentData.length > 0 ? (
                  <div>
                    <Divider />
                    <div className="overflow-y-hidden pb-125">
                      <div className="keduall-scrollbar dropdown-menu-item-wrapper">
                        {currentData
                          .map((item) => (
                            <div
                              key={item.id}
                              className={`dropdown-menu-item cursor-pointer ${item.status === NOTIFICATION_STATUSES.UNREAD
                                ? "unread-background"
                                : "read-background"
                                }`}
                              onClick={() => handleNavigate(item)}
                            >
                              <div className="flex p-3 align-items-center gap-2">
                                <div className="flex">
                                  {item.status === NOTIFICATION_STATUSES.UNREAD
                                    ? <span className="red-dot"></span>
                                    : <span className="pl-2"></span>
                                  }
                                </div>

                                <img src={item.senderAvatarUrl || logo} alt="thumbnail" className="keduall-card main-avatar-circle-25" />

                                <div className="flex-1 flex flex-column gap-1">
                                  <div className="dropdown-menu-item-content">
                                    <span>
                                      <b>{item.from}</b>
                                      <span
                                        className="keduall-typography text-16-semibold-600 text-ellipsis"
                                        dangerouslySetInnerHTML={{
                                          __html: stripHtmlTags(item.title),
                                        }}
                                      />
                                      <b>{item.to}</b>
                                    </span>
                                    <span className="ml-2 keduall-typography text-12-normal-400 text-secondary-txt">
                                      {calculateTimeLeft(item.createdTime)}{" "}
                                      {translations.ago}
                                    </span>
                                  </div>
                                  <small className="keduall-typography text-12-normal-400 text-secondary-txt">
                                    {formatDateV2(item.createdTime, DATETIME_FORMATS.YYYYMMDD)}
                                  </small> 
                                  <div className="dropdown-menu-item-content">
                                    <span
                                      className="keduall-typography text-14-normal-400 text-dark-txt text-ellipsis-line-clamp-1"
                                      dangerouslySetInnerHTML={{
                                        __html: stripHtmlTags(item.message),
                                      }}
                                    ></span>
                                  </div>
                                </div>
                              </div>
                              <Divider />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <NoData message={translations.noData} />
                )}
              </ul>
            </TabPanel>
          ))}
        </TabView>

        {!isLoading && currentData.length > 0 && (
          <Paginator
            template={paginatorTemplate}
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            className="keduall-paginator"
          />
        )}
      </div>
    </div>
  );
}
